
/* eslint-disable @typescript-eslint/camelcase */
import { Options, prop, Vue } from "vue-class-component";
import SelectSearchV2 from "@/app/ui/components/select-search/index.vue";
import { PosClientController } from "@/app/ui/controllers/PosClientController";
import { debounce } from "lodash";
import { AccountController } from "@/app/ui/controllers/AccountController";
import { PosClientList } from "@/domain/entities/PosClient";

class Props {
  title = prop<string>({
    default: "Filter",
    type: String
  });
}

@Options({
  emits: [
    "opened",
    "change",
    "reset",
    "update:modelValue",
    "changeFilter",
    "resetFilter"
  ],
  components: {
    SelectSearchV2
  },
  directives: {
    "click-outside": {
      beforeMount: function(el, binding) {
        const ourClickEventHandler = (event: any) => {
          if (!el.contains(event.target) && el !== event.target) {
            binding.value(event);
          }
        };
        el.__vueClickEventHandler__ = ourClickEventHandler;
        document.addEventListener("click", ourClickEventHandler);
      },
      unmounted: function(el) {
        document.removeEventListener("click", el.__vueClickEventHandler__);
      }
    }
  }
})
export default class Filter extends Vue.with(Props) {
  mounted() {
    if (this.isClient) {
      this.filterData.createdFor = {
        id: this.dataProfile.account_type_detail.id,
        type: this.dataProfile.account_type,
        name: this.dataProfile.account_type_detail.company_name,
        code: this.dataProfile.account_type_detail.code
      };
      this.filterData.formType = {
        name: this.$t("bulkBooking.filter.typeClient") as string,
        value: "client"
      };
    }
  }

  isChevron = false;
  openFilter = false;
  resetSelectSearch = 0;
  startDate: any = "";
  endDate: any = "";

  formatDateWithoutTime(date: any) {
    return date !== null ? this.$moment(date).format("DD MMM YYYY") : "";
  }

  setPeriode(value: Array<any>) {
    this.startDate = value[0] || "";
    this.endDate = value[1] || "";
  }

  resetfilter(value = true) {
    this.startDate = "";
    this.endDate = "";

    const picker: any = this.$refs.timeRangePicker;
    if (picker) {
      picker.reset();
      picker.selected = "";
    }

    if (this.isClient) {
      this.filterData.uploadStatus = { name: "", value: "" };
    } else {
      this.filterData = {
        uploadStatus: { name: "", value: "" },
        formType: { name: "", value: "" },
        createdFor: ""
      };
      this.resetSelectSearch++;
    }
    this.$emit("reset", value);
  }

  // filter data to be sent
  // important!! if modify this property, please update resetFilter() as well
  filterData: any = {
    uploadStatus: { name: "", value: "" },
    formType: { name: "", value: "" },
    createdFor: ""
  };

  applyFilter() {
    this.$emit("changeFilter", {
      ...this.filterData,
      startDate: this.startDate,
      endDate: this.endDate
    });
    this.openFilter = false;
  }

  resetToDefaultFilter() {
    this.resetfilter();
    this.$emit("resetFilter");
    this.openFilter = false;
  }

  get uploadStatusOptions() {
    return [
      { name: this.$t("bulkBooking.filterStatus.parent"), value: "" },
      { name: this.$t("bulkBooking.filterStatus.success"), value: "success" },
      {
        name: this.$t("bulkBooking.filterStatus.partially"),
        value: "partially"
      },
      { name: this.$t("bulkBooking.filterStatus.failed"), value: "failed" },
      {
        name: this.$t("bulkBooking.filterStatus.uploading"),
        value: "uploading"
      }
    ];
  }

  uploadStatusSelect = false;
  onOpenUploadStatus() {
    this.uploadStatusSelect = true;
  }

  onCloseUploadStatus() {
    this.uploadStatusSelect = false;
  }

  onSelectUploadStatus(name: string, value: string) {
    if (this.filterData.uploadStatus.value === value) {
      this.filterData.uploadStatus = { name: "", value: "" };
    } else {
      this.filterData.uploadStatus = { name: name, value: value };
    }
  }

  get formTypeOptions() {
    const base = [
      {
        name: this.$t("bulkBooking.filter.placeholderFileType") as string,
        value: ""
      },
      {
        name: this.$t("bulkBooking.filter.typeClient") as string,
        value: "client"
      },
      { name: this.$t("bulkBooking.filter.typeRetail") as string, value: "pos" }
    ];
    if (this.isMyPos) {
      return [
        {
          name: this.$t("bulkBooking.filter.placeholderFileType") as string,
          value: ""
        },
        {
          name: this.$t("bulkBooking.filter.typeRetail") as string,
          value: "pos"
        }
      ];
    }
    return base;
  }

  formTypeSelect = false;
  onOpenFormType() {
    this.formTypeSelect = true;
  }

  onCloseFormType() {
    this.formTypeSelect = false;
  }

  onSelectFormType(name: string, value: string) {
    PosClientController.setPosClientList(new PosClientList([]));
    this.filterData.createdFor = "";
    if (this.filterData.formType.value === value) {
      this.filterData.formType = { name: "", value: "" };
    } else {
      this.filterData.formType = { name: name, value: value };
    }
  }

  onSelectCreatedFor(value: any) {
    if (this.filterData.formType.createdFor === value) {
      this.filterData.createdFor = "";
    } else {
      this.filterData.createdFor = value;
    }
    this.$emit("change", this.filterData);
    this.$emit("update:modelValue", this.filterData);
  }

  get posClientList() {
    const list = PosClientController.posList.list.map((item) => {
      return {
        id: item.id,
        name: item.name,
        status: item.status,
        isBanned: item.isBanned,
        type: item.type
      };
    });
    list.length > 0 &&
      list.splice(0, 0, {
        id: 0,
        name: " ",
        status: "",
        isBanned: false,
        type: ""
      });
    return list;
  }

  fetchPosClient = debounce((search) => {
    if (search.length > 2 || !search) {
      const params = {
        search,
        accountType: this.filterData.formType.value || ""
      };
      PosClientController.getPosClientListV2(params);
    }
  });

  get isLoadingPosClient() {
    return PosClientController.isLoading;
  }

  get dataProfile() {
    return AccountController.accountData;
  }

  get isClient() {
    return this.dataProfile.account_type === "client";
  }

  get isInternal() {
    return this.dataProfile.account_type === "internal";
  }

  get isMyPos() {
    const acc = AccountController.accountData;
    return acc.isPosAccount && acc.countryCode === "my";
  }
}
